import type { EditTarget, LayoutBlock } from '@web-studio/shared-schemas';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickBlockField } from '../../../lib/localized';
import { parseMenuColumns, type MenuColumn, type MenuItem } from '../../../lib/parseMenuColumns';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  isMenuColumnActive,
  isMenuItemActive,
  menuItemHasContent,
  resolveItemImageUrl,
} from '../menuEditHelpers';

function DishCard({
  block,
  item,
  columnIndex,
  itemIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  block: LayoutBlock;
  item: MenuItem;
  columnIndex: number;
  itemIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  if (!menuItemHasContent(item)) {
    return null;
  }
  const itemActive = isMenuItemActive(
    activeEditTarget?.kind === 'menu-item' ? activeEditTarget : null,
    columnIndex,
    itemIndex,
  );
  const imageUrl = resolveItemImageUrl(block, item, columnIndex, itemIndex);
  const imageFieldKey = `itemImage_${columnIndex}_${itemIndex}`;
  const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : undefined;
  const imageSrcSet = imageUrl
    ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900], 78)
    : undefined;

  return (
    <article
      className={['ws-menu-dishes__card', itemActive ? 'ws-edit-child--active' : '']
        .filter(Boolean)
        .join(' ')}
      {...(editable
        ? {
            'data-edit-child': 'menu-item' as const,
            'data-column-index': columnIndex,
            'data-item-index': itemIndex,
          }
        : {})}
    >
      <div className="ws-menu-dishes__media">
        {imageUrl ? (
          <EditableMedia
            fieldKey={imageFieldKey}
            editable={editable}
            activeEditTarget={activeEditTarget}
            className="ws-menu-dishes__media-frame"
            src={imageSrc}
            srcSet={imageSrcSet}
            sizes={SITE_IMAGE_SIZES.menuDishCard}
            alt={item.name || 'Dish'}
            width={720}
            height={720}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="ws-menu-dishes__media-placeholder" aria-hidden />
        )}
      </div>
      <div className="ws-menu-dishes__body">
        <div className="ws-menu-dishes__row">
          <MenuItemOrderCode code={item.code} className="ws-menu-dishes__code" />
          <div className="ws-menu-dishes__copy">
            <div className="ws-menu-dishes__primary">
              <MenuItemName
                item={item}
                locale={locale}
                className="ws-menu-dishes__name"
                showOrderCode={false}
              />
              <MenuItemDietaryTags item={item} locale={locale} iconsOnly placement="inline" />
            </div>
            {item.description ? (
              <p className="ws-menu-dishes__desc">{item.description}</p>
            ) : null}
          </div>
          <MenuItemPriceSlot price={item.price} className="ws-menu-dishes__price" />
        </div>
      </div>
    </article>
  );
}

function DishCategory({
  block,
  column,
  columnIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  block: LayoutBlock;
  column: MenuColumn;
  columnIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  const columnTitle = column.title.trim();
  const columnActive = isMenuColumnActive(activeEditTarget, columnIndex);
  return (
    <div className="ws-menu-dishes__category">
      {columnTitle ? (
        <h3
          className={['ws-menu-dishes__category-title', columnActive ? 'ws-edit-child--active' : '']
            .filter(Boolean)
            .join(' ')}
          {...(editable
            ? {
                'data-edit-child': 'menu-column' as const,
                'data-column-index': columnIndex,
              }
            : {})}
        >
          {column.title}
        </h3>
      ) : null}
      <div className="ws-menu-dishes__grid">
        {column.items.map((item, itemIndex) => (
          <DishCard
            key={`${item.name}-${itemIndex}`}
            block={block}
            item={item}
            columnIndex={columnIndex}
            itemIndex={itemIndex}
            editable={editable}
            activeEditTarget={activeEditTarget}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Dish cards — Instagram-forward plate photography with price badge.
 */
export function MenuDishCards({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const columns = parseMenuColumns(menuDataRaw);
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-menu-dishes', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-menu-dishes__inner">
        <header className="ws-menu-dishes__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-dishes__title"
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            fieldKey="sectionSubtitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-dishes__subtitle"
          >
            {sectionSubtitle}
          </EditableContent>
        </header>
        {columns.map((column, columnIndex) => (
          <DishCategory
            key={`${column.title}-${columnIndex}`}
            block={block}
            column={column}
            columnIndex={columnIndex}
            editable={editable}
            activeEditTarget={target}
            locale={locale}
          />
        ))}
        <MenuLegalLegend columns={columns} locale={locale} />
      </div>
    </section>
  );
}
